import { StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';

import { isDefined } from '../../../../../../utils/variables';
import { ViewerEnvironmentInstance } from '../../../../../../viewer/viewer-environment';
import { pdfPageDimensions } from '../helpers/types';
import { CadDrawingReportImage } from './cad-drawing-image-utils';

type ScalebarUnit = 'm' | 'cm' | 'mm' | 'ft' | 'in';

/** How many of each unit go in one meter or foot, whichever the project's coordinate system uses. */
const fromProjectUnitMultipliers: Record<ScalebarUnit, number> = {
  m: 1,
  cm: 100,
  mm: 1000,
  ft: 1,
  in: 12,
};

export const CadDrawingScaleBar = (props: { image: CadDrawingReportImage }) => {
  const scaleBarConfiguration = getScaleBarConfig(props.image);

  // If the config is not defined it means we are in perspective view and scale bar is not applicable.
  if (!isDefined(scaleBarConfiguration)) {
    return null;
  }

  const styles = getScaleBarStyleSheet(scaleBarConfiguration.widthPixels);
  const segmentCount = getSegmentCount(scaleBarConfiguration.width);

  /**
   * Returns a red segment if even and white segment if odd.
   */
  function* segmentGenerator() {
    for (let i = 0; i < segmentCount; i++) {
      yield i % 2 === 0 ? <View style={styles.segmentRed} /> : <View style={styles.segmentWhite} />;
    }
  }

  function* measureStickGenerator(width: number) {
    const segmentLength = width / segmentCount;

    for (let i = 0; i <= segmentCount; i++) {
      yield (
        <View style={i === segmentCount ? styles.endSegment : styles.measureSegment}>
          <Text style={styles.measureLabel}>{i * segmentLength}</Text>
        </View>
      );
    }
  }

  return (
    <View style={styles.anchor}>
      <View style={styles.container}>
        <View style={styles.scaleBarContainer}>
          {[...segmentGenerator()]}

          <View style={styles.endSegment}>
            <Text style={styles.unitLabel}>{scaleBarConfiguration.unit}</Text>
          </View>
        </View>

        <View style={styles.measureSegmentContainer}>
          {[...measureStickGenerator(scaleBarConfiguration.width)]}
        </View>
      </View>
    </View>
  );
};

function getSegmentCount(width: number) {
  const segmentOptions = [
    { divisor: 150, segments: 6 },
    { divisor: 100, segments: 5 },
    { divisor: 90, segments: 9 },
    { divisor: 80, segments: 8 },
    { divisor: 70, segments: 7 },
    { divisor: 60, segments: 6 },
    { divisor: 50, segments: 5 },
    { divisor: 40, segments: 8 },
    { divisor: 30, segments: 6 },
    { divisor: 20, segments: 5 },
    { divisor: 10, segments: 5 },

    { divisor: 9, segments: 9 },
    { divisor: 8, segments: 8 },
    { divisor: 7, segments: 7 },
    { divisor: 6, segments: 6 },
    { divisor: 5, segments: 5 },
    { divisor: 4, segments: 4 },
    { divisor: 3, segments: 6 },
  ];

  // Check each option to find the best fit based on divisibility and logic.
  for (const option of segmentOptions) {
    if (width % option.divisor === 0) {
      return option.segments;
    }
  }

  return 10;
}

/**
 * Picks a unit that makes a scale bar of the given width, in the project's unit, easy to read.
 */
function getScaleBarUnit(width: number): ScalebarUnit {
  if (ViewerEnvironmentInstance.getSpatialReference().linearUnit === 'ft') {
    return width <= 1 ? 'in' : 'ft';
  }

  return width <= 0.3 ? 'mm' : width <= 3 ? 'cm' : 'm';
}

/**
 * The width should dynamically adjust to a value that makes sense to measure with.
 */
function calculateScaleBarWidth(imageWidthPixels: number, gsd: number) {
  // Calculate the initial width of the scale bar about 30% of the total width of the page.
  const widthPercent = 0.3;
  let value = imageWidthPixels * gsd * widthPercent;

  const unit = getScaleBarUnit(value);

  value *= fromProjectUnitMultipliers[unit];

  /**
   * Rounds the value based on the multiplier.
   * Always returns at least 1
   */
  function calculate(multiplier: number) {
    return Math.max(1, Math.round(value / multiplier) * multiplier);
  }

  const roundingMap = new Map<number, number>([
    [9, 1], // Case 1, round to nearest whole digit
    [30, 5], // Case 2, round to nearest 5
    [95, 10], // Case 3, round to nearest 10
  ]);

  for (const [treshold, multiplier] of roundingMap.entries()) {
    if (!isDefined(treshold) || value <= treshold) {
      return {
        value: calculate(multiplier),
        unit,
      };
    }
  }

  // For all other cases, round to nearest 50.
  return { value: calculate(50), unit };
}

function getScaleBarConfig(image: CadDrawingReportImage) {
  if (!isDefined(image.gsd)) {
    return undefined;
  }

  const { value, unit } = calculateScaleBarWidth(image.width, image.gsd);

  /**
   * If the value for some reason is less or equal to 3 mm we will hide the scale bar to
   * prevent very small decimal values to be displayed.
   *
   * At max zoom level the scale bar was 40 mm wide. (checked 2024-03-06)
   * But this may vary depending on monitor and size of the browser window.
   */
  if (value <= 3 && unit === 'mm') {
    return undefined;
  }

  const imageWidthInDocument =
    pdfPageDimensions.a3.landscape72Dpi.width -
    pdfPageDimensions.a3.landscape72Dpi.defaultPadding * 2;
  const widthInProjectUnits = value / fromProjectUnitMultipliers[unit];

  // Calcualate the page's GSD value based on the GSD of the image and the new scale..
  const pageGsd = image.gsd * (image.width / imageWidthInDocument);

  return {
    widthPixels: widthInProjectUnits / pageGsd,
    width: value,
    unit,
  };
}

function getScaleBarStyleSheet(scaleBarWidth: number) {
  const scaleBarHeight = 10;
  return StyleSheet.create({
    /**
     * Anchors the scale bar at the bottom of the frame
     * aligning it with the info panel.
     */
    anchor: {
      position: 'absolute',
      left: 0,
      bottom: `0`,
    },
    container: {
      position: 'absolute',
      bottom: '0',
      left: '0',
      padding: '6px 36px 14px 6px',
      backgroundColor: 'white',
      boxSizing: 'border-box',
      border: '1px solid #a5acaf',
      display: 'flex',
      flexDirection: 'column',
    },
    scaleBarContainer: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
      backgroundColor: 'white',
      border: '1px solid #a5acaf',
      height: `${scaleBarHeight}px`,
      width: `${scaleBarWidth}px`,
      boxSizing: 'border-box',
    },
    segmentWhite: {
      position: 'relative',
      backgroundColor: 'white',
      flex: 1,
      height: '100%',
    },
    segmentRed: {
      position: 'relative',
      backgroundColor: '#c15a5a',
      flex: 1,
      height: '100%',
    },
    labelLeft: {
      top: '2px',
      left: '4px',
      fontSize: '12px',
      position: 'absolute',
    },
    labelRight: {
      top: '2px',
      right: '-16px',
      fontSize: '12px',
      position: 'absolute',
    },
    measureSegmentContainer: {
      position: 'relative',
      display: 'flex',
      borderLeft: '1px solid #a5acaf',
      width: `${scaleBarWidth}px`,
      height: `6px`,
      boxSizing: 'border-box',
      flexDirection: 'row',
      backgroundColor: 'white',
    },
    measureSegment: {
      position: 'relative',
      display: 'flex',
      borderRight: '1px solid #a5acaf',
      flex: 1,
      height: '100%',
      boxSizing: 'border-box',
    },
    endSegment: {
      position: 'relative',
      flex: 0,
      height: '100%',
    },
    unitLabel: {
      top: '-4px',
      left: '4px',
      fontSize: '12px',
      position: 'absolute',
    },
    measureLabel: {
      top: '2px',
      left: '2px',
      fontSize: '12px',
      position: 'absolute',
    },
  });
}
